/**
 * useReferralEntry — une entrée du parrainage : le geste qui ouvre la page, et le gain à écrire.
 *
 * Deux lecteurs : le bandeau en bas de l'accueil et la ligne « Parrainer une famille » du portail
 * Abonnement. Ils ont remplacé le cœur du header et prennent **son chemin** — même route
 * (`referrer/dashboard` avec le hash du parent), même événement (`referral.dashboard_opened`,
 * complété de la surface d'où il part), qui ouvre le tableau de bord du design system.
 *
 * Pas d'adresse de tableau de bord ⇒ `onOpenReferral` absent ⇒ pas d'entrée : un bouton qui ne
 * mène nulle part vaut moins que pas de bouton (la règle du cœur).
 */
import { useMemo } from "react"
import { useNavigation } from "@react-navigation/native"
import { useSelector } from "react-redux"
import * as amplitude from "@amplitude/analytics-react-native"

import { referralEntryViewModel } from "../../view-models/parent/Referral/referralEntryViewModel"

/** La surface d'où part le geste — seulement pour l'événement Amplitude. */
export type ReferralEntrySource = "home_banner" | "subscription_portal"

export type ReferralEntry = {
    onOpenReferral?: () => void
    /** « 50€ » — absent quand le gain n'est pas connu : l'entrée garde alors sa phrase sans montant. */
    rewardLabel?: string
}

export function useReferralEntry(source: ReferralEntrySource): ReferralEntry {
    const { referralHash, rewardLabel } = useSelector(referralEntryViewModel)
    const navigation = useNavigation<{ navigate: (name: string, params: { h: string }) => void }>()

    const onOpenReferral = useMemo(() => {
        if (!referralHash) return undefined
        return () => {
            amplitude.track("referral.dashboard_opened", { source })
            navigation.navigate("referrer/dashboard", { h: referralHash })
        }
    }, [navigation, referralHash, source])

    return { onOpenReferral, rewardLabel }
}
